import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { TCHA_LOGO_BASE64 } from './logo';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

// TCHA Brand colors
const COLORS = {
  red: '#c8102e',
  redDark: '#a10d25',
  gray: '#808080',
  grayLight: '#f3f4f6',
  grayDark: '#374151',
  text: '#1f2937',
  white: '#ffffff',
};

function formatCurrency(amount) {
  return '$' + (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function yearStartISO() {
  const year = new Date().getFullYear();
  return `${year}-01-01`;
}

export default function Dashboard({ onNavigateToInvoice, onViewInvoices }) {
  const [ytd, setYtd] = useState({ total: 0, count: 0, loading: true });
  const [last30, setLast30] = useState({ total: 0, count: 0, loading: true });
  const [customRange, setCustomRange] = useState({
    from: daysAgoISO(30),
    to: todayISO(),
    total: 0,
    count: 0,
    loading: false,
    hasResult: false,
  });
  const [error, setError] = useState('');

  const fetchRangeTotal = useCallback(async (fromDate, toDate) => {
    const { data, error: err } = await supabase
      .from('TCHA_invoices')
      .select('total_amount')
      .gte('invoice_date', fromDate)
      .lte('invoice_date', toDate);

    if (err) throw err;

    const total = (data || []).reduce((sum, row) => sum + (parseFloat(row.total_amount) || 0), 0);
    return { total, count: (data || []).length };
  }, []);

  const loadYTD = useCallback(async () => {
    try {
      setYtd(prev => ({ ...prev, loading: true }));
      const result = await fetchRangeTotal(yearStartISO(), todayISO());
      setYtd({ ...result, loading: false });
    } catch (err) {
      console.error('Error loading YTD income:', err);
      setError('Could not load income data. Check database connection.');
      setYtd({ total: 0, count: 0, loading: false });
    }
  }, [fetchRangeTotal]);

  const loadLast30 = useCallback(async () => {
    try {
      setLast30(prev => ({ ...prev, loading: true }));
      const result = await fetchRangeTotal(daysAgoISO(30), todayISO());
      setLast30({ ...result, loading: false });
    } catch (err) {
      console.error('Error loading last 30 days income:', err);
      setLast30({ total: 0, count: 0, loading: false });
    }
  }, [fetchRangeTotal]);

  useEffect(() => {
    loadYTD();
    loadLast30();
  }, [loadYTD, loadLast30]);

  const handleCustomRangeSubmit = async () => {
    if (!customRange.from || !customRange.to) return;
    try {
      setCustomRange(prev => ({ ...prev, loading: true }));
      const result = await fetchRangeTotal(customRange.from, customRange.to);
      setCustomRange(prev => ({ ...prev, ...result, loading: false, hasResult: true }));
    } catch (err) {
      console.error('Error loading custom range income:', err);
      setCustomRange(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <img src={TCHA_LOGO_BASE64} alt="TCHA Logo" style={styles.logo} />
          <div>
            <h1 style={styles.title}>The Canadian Health Academy</h1>
            <p style={styles.subtitle}>Invoice Dashboard</p>
          </div>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Cards Grid */}
        <div style={styles.cardsGrid}>
          {/* YTD Card */}
          <div style={styles.card}>
            <div style={styles.cardLabel}>Year-to-Date Income</div>
            <div style={styles.cardValue}>
              {ytd.loading ? '...' : formatCurrency(ytd.total)}
            </div>
            <div style={styles.cardMeta}>
              {ytd.loading ? 'Loading...' : `${ytd.count} invoice${ytd.count !== 1 ? 's' : ''} in ${new Date().getFullYear()}`}
            </div>
          </div>

          {/* Last 30 Days Card */}
          <div style={styles.card}>
            <div style={styles.cardLabel}>Last 30 Days</div>
            <div style={styles.cardValue}>
              {last30.loading ? '...' : formatCurrency(last30.total)}
            </div>
            <div style={styles.cardMeta}>
              {last30.loading ? 'Loading...' : `${last30.count} invoice${last30.count !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>

        {/* Custom Date Range Card */}
        <div style={styles.rangeCard}>
          <div style={styles.cardLabel}>Custom Date Range</div>
          <div style={styles.rangeInputs}>
            <div style={styles.rangeInputGroup}>
              <label style={styles.rangeLabel}>From</label>
              <input
                type="date"
                value={customRange.from}
                onChange={(e) => setCustomRange(prev => ({ ...prev, from: e.target.value }))}
                style={styles.dateInput}
              />
            </div>
            <div style={styles.rangeInputGroup}>
              <label style={styles.rangeLabel}>To</label>
              <input
                type="date"
                value={customRange.to}
                onChange={(e) => setCustomRange(prev => ({ ...prev, to: e.target.value }))}
                style={styles.dateInput}
              />
            </div>
            <button
              style={styles.rangeButton}
              onClick={handleCustomRangeSubmit}
              disabled={customRange.loading}
            >
              {customRange.loading ? 'Loading...' : 'Get Total'}
            </button>
          </div>

          {customRange.hasResult && (
            <div style={styles.rangeResult}>
              <div style={styles.rangeResultValue}>{formatCurrency(customRange.total)}</div>
              <div style={styles.cardMeta}>
                {customRange.count} invoice{customRange.count !== 1 ? 's' : ''} from {customRange.from} to {customRange.to}
              </div>
            </div>
          )}
        </div>

        {/* Navigate Buttons */}
        <div style={styles.buttonRow}>
          <button style={styles.createButton} onClick={onNavigateToInvoice}>
            + Create New Invoice
          </button>
          <button style={styles.secondaryButton} onClick={onViewInvoices}>
            View / Edit Invoices
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(to bottom right, ${COLORS.grayLight}, #e5e7eb)`,
    padding: '1rem',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  container: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
    padding: '1.25rem',
    background: COLORS.white,
    borderRadius: '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  logo: {
    width: '56px',
    height: '56px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: COLORS.text,
    margin: 0,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: '0.875rem',
    color: COLORS.gray,
    margin: '0.25rem 0 0 0',
  },
  errorBox: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '0.875rem 1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    border: '1px solid #fca5a5',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  card: {
    background: COLORS.white,
    borderRadius: '0.75rem',
    padding: '1.5rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderTop: `4px solid ${COLORS.red}`,
  },
  cardLabel: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: COLORS.gray,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    marginBottom: '0.5rem',
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: 800,
    color: COLORS.text,
    lineHeight: 1.1,
    marginBottom: '0.375rem',
    wordBreak: 'break-word',
  },
  cardMeta: {
    fontSize: '0.8125rem',
    color: COLORS.gray,
  },
  rangeCard: {
    background: COLORS.white,
    borderRadius: '0.75rem',
    padding: '1.5rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderTop: `4px solid ${COLORS.gray}`,
    marginBottom: '1.5rem',
  },
  rangeInputs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'end',
    marginTop: '0.75rem',
  },
  rangeInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 140px',
    minWidth: '140px',
  },
  rangeLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: COLORS.grayDark,
    marginBottom: '0.375rem',
  },
  dateInput: {
    padding: '0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    fontSize: '0.9375rem',
    fontFamily: 'inherit',
  },
  rangeButton: {
    padding: '0.625rem 1.25rem',
    background: COLORS.red,
    color: COLORS.white,
    border: 'none',
    borderRadius: '0.5rem',
    fontWeight: 700,
    fontSize: '0.9375rem',
    cursor: 'pointer',
    flex: '1 1 140px',
    minWidth: '140px',
  },
  rangeResult: {
    marginTop: '1.25rem',
    paddingTop: '1.25rem',
    borderTop: '1px solid #e5e7eb',
  },
  rangeResultValue: {
    fontSize: '1.75rem',
    fontWeight: 800,
    color: COLORS.red,
    marginBottom: '0.25rem',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  createButton: {
    width: '100%',
    padding: '1rem',
    background: `linear-gradient(135deg, ${COLORS.red}, ${COLORS.redDark})`,
    color: COLORS.white,
    border: 'none',
    borderRadius: '0.75rem',
    fontWeight: 700,
    fontSize: '1.0625rem',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(200, 16, 46, 0.35)',
  },
  secondaryButton: {
    width: '100%',
    padding: '1rem',
    background: COLORS.white,
    color: COLORS.grayDark,
    border: '1px solid #d1d5db',
    borderRadius: '0.75rem',
    fontWeight: 700,
    fontSize: '1.0625rem',
    cursor: 'pointer',
  },
};
